import { useEffect, useRef } from 'react';
import { useReducedMotion } from 'framer-motion';
import { Mesh, Program, Renderer, Triangle } from 'ogl';

import { MILLISECONDS_TO_SECONDS } from '@/constants/time';

const VERTEX_SHADER = `#version 300 es
in vec2 position;
void main() {
  gl_Position = vec4(position, 0.0, 1.0);
}
`;

const FRAGMENT_SHADER = `#version 300 es
precision highp float;

uniform float uTime;
uniform float uAmplitude;
uniform vec2 uResolution;
uniform float uBlend;

out vec4 fragColor;

vec3 permute(vec3 x) {
  return mod(((x * 34.0) + 1.0) * x, 289.0);
}

float snoise(vec2 v){
  const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
  vec2 i  = floor(v + dot(v, C.yy));
  vec2 x0 = v - i + dot(i, C.xx);
  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;
  i = mod(i, 289.0);
  vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));
  vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);
  m = m * m;
  m = m * m;
  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);
  vec3 g;
  g.x = a0.x * x0.x + h.x * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

void main() {
  vec2 uv = gl_FragCoord.xy / uResolution;
  vec3 auroraBaseColor = vec3(1.0);

  float lowerWave = snoise(vec2(uv.x * 2.0 + uTime * 0.1, uTime * 0.25)) * 0.5 * uAmplitude;
  float upperWave = snoise(vec2(uv.x * 2.4 - uTime * 0.08, uTime * 0.18 + 7.13)) * 0.35 * uAmplitude;
  float bottomBoundary = exp(lowerWave) * 0.42;
  float topBoundary = 1.0 - exp(upperWave) * 0.34;
  float bodyMask = smoothstep(bottomBoundary - uBlend, bottomBoundary + uBlend, uv.y) * (1.0 - smoothstep(topBoundary - uBlend, topBoundary + uBlend, uv.y));
  float intensity = bodyMask * (1.4 + 0.55 * uv.y);
  vec3 auroraColor = intensity * auroraBaseColor * 1.6;
  float auroraAlpha = min(1.0, bodyMask * 1.45);
  fragColor = vec4(auroraColor * auroraAlpha, auroraAlpha);
}
`;

interface VideoPlayerAnalyzingAuroraProps {
    amplitude: number;
    blend: number;
    speed: number;
    heightReductionPx?: number;
}

export function VideoPlayerAnalyzingAurora({
    amplitude,
    blend,
    speed,
    heightReductionPx = 0,
}: VideoPlayerAnalyzingAuroraProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const amplitudeRef = useRef(amplitude);
    const blendRef = useRef(blend);
    const speedRef = useRef(speed);
    const heightReductionRef = useRef(heightReductionPx);
    const shouldReduceMotion = useReducedMotion();

    amplitudeRef.current = amplitude;
    blendRef.current = blend;
    speedRef.current = speed;
    heightReductionRef.current = heightReductionPx;

    useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        const renderer = new Renderer({
            alpha: true,
            premultipliedAlpha: true,
            antialias: true,
        });
        const gl = renderer.gl;
        gl.clearColor(0, 0, 0, 0);
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
        gl.canvas.style.backgroundColor = 'transparent';

        const geometry = new Triangle(gl);
        if (geometry.attributes.uv) {
            delete geometry.attributes.uv;
        }

        const program = new Program(gl, {
            vertex: VERTEX_SHADER,
            fragment: FRAGMENT_SHADER,
            uniforms: {
                uTime: { value: 0 },
                uAmplitude: { value: amplitudeRef.current },
                uResolution: {
                    value: [container.offsetWidth, container.offsetHeight],
                },
                uBlend: { value: blendRef.current },
            },
        });

        const mesh = new Mesh(gl, { geometry, program });
        container.appendChild(gl.canvas);

        const renderStaticFrame = () => {
            program.uniforms.uTime.value = 0;
            program.uniforms.uAmplitude.value = amplitudeRef.current;
            program.uniforms.uBlend.value = blendRef.current;
            renderer.render({ scene: mesh });
        };

        const updateSize = () => {
            const width = container.offsetWidth;
            const height = Math.max(
                1,
                container.offsetHeight - heightReductionRef.current,
            );
            renderer.setSize(width, height);
            program.uniforms.uResolution.value = [width, height];
            if (shouldReduceMotion) {
                renderStaticFrame();
            }
        };

        let animationFrameId = 0;
        const render = (time: number) => {
            animationFrameId = requestAnimationFrame(render);
            program.uniforms.uTime.value =
                (time / MILLISECONDS_TO_SECONDS) * speedRef.current;
            program.uniforms.uAmplitude.value = amplitudeRef.current;
            program.uniforms.uBlend.value = blendRef.current;
            renderer.render({ scene: mesh });
        };

        updateSize();
        window.addEventListener('resize', updateSize);
        if (!shouldReduceMotion) {
            animationFrameId = requestAnimationFrame(render);
        }

        return () => {
            cancelAnimationFrame(animationFrameId);
            window.removeEventListener('resize', updateSize);
            if (gl.canvas.parentNode === container) {
                container.removeChild(gl.canvas);
            }
            gl.getExtension('WEBGL_lose_context')?.loseContext();
        };
    }, [shouldReduceMotion]);

    return <div ref={containerRef} className="w-full h-full" />;
}
